import React, { lazy, Suspense } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { withPermissionsAndRoles, withSession } from "./session";
import { APP_ROLES } from "./constants";

const TemplateManagerLL = lazy(() => import("./templateManager/templateManager"));
const DiscoveryManagerLL = lazy(() => import("./discoveryManager/discoveryManager"));
const MemberManagementLL = lazy(() => import("./members/members"));
const ClientProjectsListLL = lazy(() => import("./clientProjects/clientProjectsList"));
const AccessDeniedLL = lazy(() => import("./layout/accessDenied"));
const BannerMessageLL = lazy(() => import("./layout/bannerMessage"));
const RolesLL = lazy(() => import("./Roles/Roles"));
const ProjectsLL = lazy(() => import("./projects/projects"));
const ClientOrgLL = lazy(() => import("./Clients/ClientOrgs"));
const ProjectDetailsLL = lazy(() => import("./projects/projectDetails"));
const CertificatesLL = lazy(() => import("./certificates/certificates"));
const ClientProjectDetailLL = lazy(() => import("./clientProjects/clientProjectDetails"));
const RelatedGuidesLL = lazy(() =>
  import("shared-one-ux-components").then((module) => ({ default: module.RelatedGuides }))
);
const ChangeHistoryLL = lazy(() => import("./projects/apiDiscovery/changeHistoryPage"));
const SIPartnerAdminViewLL = lazy(() => import("./systemIntegrators/siPartnersAdmin"));
const SIPartnersViewLL = lazy(() => import("./systemIntegrators/siPartners"));
const SiProjectDetailsLL = lazy(() => import("./projects/siProjectDetails"));
const SIPartnerProjectDetailsLL = lazy(() =>
  import("./systemIntegrators/adminSiProjectDetails/siPartnerProjectDetails")
);
const McpProjectDetailLL = lazy(() => import("./mcpProjects/mcpProjectDetail"));
const McpCapabilitiesLL = lazy(() => import("./mcpProjects/mcpCapabilities"));
const McpRemoveCapabilitiesLL = lazy(() => import("./mcpProjects/mcpRemoveCapabilities"));
const McpChangeHistoryLL = lazy(() => import("./mcpProjects/mcpChangeHistory"));
const ConnectorsLL = lazy(() => import("./connectors/connectors"));
const ProjectsESILL = lazy(() => import("./projects/projectsESI"));

const lazyLoadComponent = (Component) => (props) => (
  <Suspense fallback={null}>
    <Component {...props} />
  </Suspense>
);

const TemplateManager = lazyLoadComponent(TemplateManagerLL);
const DiscoveryManager = lazyLoadComponent(DiscoveryManagerLL);
const MemberManagement = lazyLoadComponent(MemberManagementLL);
const ClientProjectsList = lazyLoadComponent(ClientProjectsListLL);
const AccessDenied = lazyLoadComponent(AccessDeniedLL);
const BannerMessage = lazyLoadComponent(BannerMessageLL);
const Roles = lazyLoadComponent(RolesLL);
const Projects = lazyLoadComponent(ProjectsLL);
const ClientOrg = lazyLoadComponent(ClientOrgLL);
const ProjectDetails = lazyLoadComponent(ProjectDetailsLL);
const Certificates = lazyLoadComponent(CertificatesLL);
const ClientProjectDetail = lazyLoadComponent(ClientProjectDetailLL);
const RelatedGuides = lazyLoadComponent(RelatedGuidesLL);
const ChangeHistory = lazyLoadComponent(ChangeHistoryLL);
const SIPartnerAdminView = lazyLoadComponent(SIPartnerAdminViewLL);
const SIPartnersView = lazyLoadComponent(SIPartnersViewLL);
const SiProjectDetails = lazyLoadComponent(SiProjectDetailsLL);
const SIPartnerProjectDetails = lazyLoadComponent(SIPartnerProjectDetailsLL);
const McpProjectDetail = lazyLoadComponent(McpProjectDetailLL);
const McpCapabilities = lazyLoadComponent(McpCapabilitiesLL);
const McpRemoveCapabilities = lazyLoadComponent(McpRemoveCapabilitiesLL);
const McpChangeHistory = lazyLoadComponent(McpChangeHistoryLL);
const Connectors = lazyLoadComponent(ConnectorsLL);
const ProjectsESI = lazyLoadComponent(ProjectsESILL);

const BaseRoutes = ({ roles, session }) => {
  const routes = [];
  const location = useLocation();
  const { pathname } = location;
  const { region, isCAOrg } = session;

  if (roles.includes(APP_ROLES.CLIENT_ADMIN) || roles.includes(APP_ROLES.ESI_CLIENT_ADMIN)) {
    routes.push(
      <Route
        key={"route-key-root-client-orgs"}
        path={"/"}
        element={<Navigate to="/client-orgs" replace />}
      />,
      <Route
        key={"route-key-client-orgs"}
        path={"/client-orgs"}
        element={
          <ClientOrg
            session={session}
            region={region}
            role={session.role}
            roles={roles}
            ooid={session.organizationOID}
            orgName={session.organizationName}
          />
        }
      />,
      <Route
        key={"route-key-client-projects"}
        path={"/client-projects"}
        element={<ClientProjectsList roles={roles} />}
      />,
      <Route
        key={"route-key-client-project-detail"}
        path={"/:region/client-projects/:projectID"}
        element={<ClientProjectDetail region={region} />}
      />,
      <Route
        key={"route-key-mcp-project-detail-admin"}
        path={"/mcp-projects/:projectID"}
        element={<McpProjectDetail />}
      />,
      <Route
        key={"route-key-mcp-capabilities-admin"}
        path={"/mcp-projects/:projectID/capabilities"}
        element={<McpCapabilities />}
      />,
      <Route
        key={"route-key-mcp-remove-capabilities-admin"}
        path={"/mcp-projects/:projectID/capabilities/remove"}
        element={<McpRemoveCapabilities />}
      />,
      <Route
        key={"route-key-mcp-change-history-admin"}
        path={"/mcp-projects/:projectID/change-history"}
        element={<McpChangeHistory />}
      />
    );

    if (roles.includes(APP_ROLES.CLIENT_ADMIN) || roles.includes(APP_ROLES.ESI_CLIENT_ADMIN)) {
      routes.push(
        <Route
          key={"route-key-si-partners"}
          path={"/si-partners"}
          element={<SIPartnerAdminView roles={roles} />}
        />
      );
    }
    if (roles.includes(APP_ROLES.CLIENT_ADMIN)) {
      routes.push(
        <Route
          key={"route-key-si-partner-projects"}
          path={"/si-partner-projects/:projectID"}
          element={<SIPartnerProjectDetails />}
        />
      );
    }
  }
  if (roles.includes(APP_ROLES.TEMPLATE_ADMIN) || roles.includes(APP_ROLES.ESI_TEMPLATE_ADMIN)) {
    routes.push(
      <Route
        key={"route-key-root-widgets"}
        path={"/"}
        element={<Navigate to="/template-manager" replace />}
      />,
      <Route
        key={"route-key-template-manager"}
        path={"/template-manager"}
        element={<TemplateManager region={region} roles={roles} />}
      />
    );
  }
  if (roles.includes(APP_ROLES.TEMPLATE_ADMIN)) {
    routes.push(
      <Route
        key={"route-key-discovery-manager"}
        path={"/discovery-manager"}
        element={<DiscoveryManager roles={roles} session={session} />}
      />
    );
  }
  if (roles.includes(APP_ROLES.MEMBER_USER) || roles.includes(APP_ROLES.ESI_MEMBER_USER)) {
    routes.push(
      <Route
        key={"route-key-root-members"}
        path={"/"}
        element={<Navigate to="/members" replace />}
      />,
      <Route
        key={"route-key-members"}
        path={"/members"}
        element={
          <MemberManagement
            role={session.role}
            aoid={session.associateOID}
            orgName={session.organizationName}
            isAdmin={false}
            region={region}
          />
        }
      />
    );
  }
  if (roles.includes(APP_ROLES.PROJECT_USER) || roles.includes(APP_ROLES.ESI_PROJECT_USER)) {
    routes.push(
      <Route
        key={"route-key-root-projects"}
        path={"/"}
        element={<Navigate to="/projects" replace />}
      />,
      <Route
        key={"route-key-projects"}
        path={"/projects"}
        element={
          roles.includes(APP_ROLES.ESI_PROJECT_USER) ? (
            <ProjectsESI
              givenName={session.given_name}
              orgName={session.organizationName}
              region={region}
              roles={roles}
            />
          ) : (
            <Projects
              givenName={session.given_name}
              orgName={session.organizationName}
              region={region}
              roles={roles}
              isCAOrg={isCAOrg}
            />
          )
        }
      />,
      <Route
        key={"route-key-project-details"}
        path={"/projects/:projectID"}
        element={
          <ProjectDetails
            role={session.role}
            roles={roles}
            ooid={session.organizationOID}
            aoid={session.associateOID}
            orgName={session.organizationName}
            region={region}
            locale={session.locale}
          />
        }
      />,
      <Route
        key={"route-key-related-guides"}
        path={"/projects/:projectID/related-guides"}
        element={<RelatedGuides location={location}/>}
      />,
      <Route
        key={"route-key-change-history"}
        path={"/projects/:projectID/change-history"}
        element={<ChangeHistory />}
      />,
      <Route
        key={"route-key-mcp-project-detail"}
        path={"/mcp-projects/:projectID"}
        element={<McpProjectDetail />}
      />,
      <Route
        key={"route-key-mcp-capabilities"}
        path={"/mcp-projects/:projectID/capabilities"}
        element={<McpCapabilities />}
      />,
      <Route
        key={"route-key-mcp-remove-capabilities"}
        path={"/mcp-projects/:projectID/capabilities/remove"}
        element={<McpRemoveCapabilities />}
      />,
      <Route
        key={"route-key-mcp-change-history"}
        path={"/mcp-projects/:projectID/change-history"}
        element={<McpChangeHistory />}
      />,
      <Route
        key={"route-key-connectors"}
        path={"/connectors"}
        element={<Connectors />}
      />
    );
    if (roles.includes(APP_ROLES.PROJECT_USER) && !isCAOrg || roles.includes(APP_ROLES.ESI_PROJECT_USER)) {
      routes.push(
        <Route
          key={"route-key-partners"}
          path={"/partners"}
          element={<SIPartnersView roles={roles} />}
        />
      );
    }
    if (roles.includes(APP_ROLES.PROJECT_USER)) {
      routes.push(
        <Route
          key={"route-key-si-project-details"}
          path={"/si-projects/:projectID"}
          element={<SiProjectDetails memberType={session.memberType} />}
        />
      );
    }
  }
  if (roles.includes(APP_ROLES.CERTIFICATE_USER) || roles.includes(APP_ROLES.ESI_CERTIFICATE_USER)) {
    routes.push(
      <Route
        key={"route-key-root-certificates"}
        path={"/"}
        element={<Navigate to="/certificate" replace />}
      />,
      <Route
        key={"route-key-certificates"}
        path={"/certificate"}
        element={
          <Certificates
            role={session.role}
            roles={roles}
            ooid={session.organizationOID}
            aoid={session.associateOID}
            orgName={session.organizationName}
            email={session.email}
            region={region}
          />
        }
      />
    );
  }
  if (roles.includes(APP_ROLES.SUPER_ADMIN) || roles.includes(APP_ROLES.ESI_SUPER_ADMIN)) {
    routes.push(
      <Route
        key={"route-key-root-roles"}
        path={"/"}
        element={<Navigate to="/roles" replace />}
      />,
      <Route
        key={"route-key-template-roles"}
        path={"/roles"}
        element={
          <Roles
            role={session.role}
            roles={roles}
            ooid={session.organizationOID}
            orgName={session.organizationName}
            region={region}
          />
        }
      />,
      <Route
        key={"route-key-mcp-project-detail-superadmin"}
        path={"/mcp-projects/:projectID"}
        element={<McpProjectDetail />}
      />,
      <Route
        key={"route-key-mcp-capabilities-superadmin"}
        path={"/mcp-projects/:projectID/capabilities"}
        element={<McpCapabilities />}
      />,
      <Route
        key={"route-key-mcp-remove-capabilities-superadmin"}
        path={"/mcp-projects/:projectID/capabilities/remove"}
        element={<McpRemoveCapabilities />}
      />,
      <Route
        key={"route-key-mcp-change-history-superadmin"}
        path={"/mcp-projects/:projectID/change-history"}
        element={<McpChangeHistory />}
      />
    );
  }

  routes.push(
    <Route
      key={"route-key-access-denied"}
      path={"/access-denied"}
      element={<AccessDenied roles={roles} />}
    />
  );
  routes.push(
    <Route
      key={"redirect-key-access-denied"}
      path={"/*"}
      element={
        <Navigate
          to={`/access-denied?error=${pathname === "/error"}`}
          replace
        />
      }
    />
  );

  return (
    <div className="framework-masthead__page">
      <Routes>{routes}</Routes>
    </div>
  );
};

export default withSession(withPermissionsAndRoles(BaseRoutes));
